/**
 * CR316: Search Suggestions Hook
 * 
 * Generates suggestions from card-visible deal fields only.
 * CRITICAL: Never includes terms, fine print, or evidence blobs.
 */

import { useMemo } from "react";
import type { SearchSuggestionsConfig } from "@/lib/layout-studio/types";
import { DEFAULT_SEARCH_SUGGESTIONS_CONFIG } from "@/lib/layout-studio/types";
import type { SuggestionItem } from "@/components/layout/blocks/SearchDropdownPanel";

interface Deal {
  id?: string;
  canonical_deal_json?: {
    merchant_name?: string;
    purchase_brand?: string;
    reward_merchant?: string;
    title?: string;
    [key: string]: unknown;
  };
  // Fallback fields
  merchant_name?: string;
  title?: string;
  [key: string]: unknown;
}

interface UseSearchSuggestionsOptions {
  deals: Deal[];
  query: string;
  config?: SearchSuggestionsConfig;
}

/**
 * Extract card-visible suggestion values from deals.
 * ONLY uses: merchant_name, purchase_brand, reward_merchant, title
 * NEVER uses: terms, fine_print, evidence, raw_text, etc.
 */
export function useSearchSuggestions({
  deals,
  query,
  config,
}: UseSearchSuggestionsOptions): SuggestionItem[] {
  const mergedConfig = { ...DEFAULT_SEARCH_SUGGESTIONS_CONFIG, ...config };
  
  return useMemo(() => {
    if (!mergedConfig.enabled) return [];
    
    const trimmedQuery = query.trim().toLowerCase();
    if (trimmedQuery.length < mergedConfig.minChars) return [];
    
    // Collect unique values by type with counts
    const merchantMap = new Map<string, number>();
    const brandMap = new Map<string, number>();
    const categoryMap = new Map<string, number>(); // We'll derive from merchant categories if available
    const titleMap = new Map<string, number>();
    
    for (const deal of deals) {
      const json = deal.canonical_deal_json || {};
      
      // Merchant (card-visible)
      const merchant = json.merchant_name || deal.merchant_name;
      if (merchant && mergedConfig.typesEnabled.merchant) {
        merchantMap.set(merchant, (merchantMap.get(merchant) || 0) + 1);
      }
      
      // Brand (purchase_brand or reward_merchant - card-visible)
      if (mergedConfig.typesEnabled.brand) {
        const purchaseBrand = json.purchase_brand;
        const rewardMerchant = json.reward_merchant;
        if (purchaseBrand) {
          brandMap.set(purchaseBrand, (brandMap.get(purchaseBrand) || 0) + 1);
        }
        if (rewardMerchant && rewardMerchant !== merchant) {
          brandMap.set(rewardMerchant, (brandMap.get(rewardMerchant) || 0) + 1);
        }
      }
      
      // Title (card-visible)
      const title = json.title || deal.title;
      if (title && mergedConfig.typesEnabled.title) {
        titleMap.set(title, (titleMap.get(title) || 0) + 1);
      }
    }
    
    // Match function based on mode
    const matches = (value: string): boolean => {
      const lowerValue = value.toLowerCase();
      switch (mergedConfig.matchMode) {
        case "starts_with":
          return lowerValue.startsWith(trimmedQuery);
        case "contains":
          return lowerValue.includes(trimmedQuery);
        case "starts_with_then_contains":
        default:
          // Will be sorted: starts_with first, then contains
          return lowerValue.includes(trimmedQuery);
      }
    };
    
    const startsWithQuery = (value: string): boolean => {
      return value.toLowerCase().startsWith(trimmedQuery);
    };
    
    // Convert maps to suggestion items
    const suggestions: SuggestionItem[] = [];
    
    merchantMap.forEach((count, text) => {
      if (matches(text)) {
        suggestions.push({ text, type: "merchant", count });
      }
    });
    
    brandMap.forEach((count, text) => {
      if (matches(text)) {
        suggestions.push({ text, type: "brand", count });
      }
    });
    
    categoryMap.forEach((count, text) => {
      if (matches(text)) {
        suggestions.push({ text, type: "category", count });
      }
    });
    
    titleMap.forEach((count, text) => {
      if (matches(text)) {
        suggestions.push({ text, type: "title", count });
      }
    });
    
    // Sort: starts_with first, then by count descending, then alphabetically
    suggestions.sort((a, b) => {
      const aStarts = startsWithQuery(a.text) ? 0 : 1;
      const bStarts = startsWithQuery(b.text) ? 0 : 1;
      if (aStarts !== bStarts) return aStarts - bStarts;
      
      // Then by count (higher first)
      if ((b.count || 0) !== (a.count || 0)) {
        return (b.count || 0) - (a.count || 0);
      }
      
      // Then alphabetically
      return a.text.localeCompare(b.text);
    });
    
    return suggestions.slice(0, mergedConfig.maxItems);
  }, [deals, query, mergedConfig]);
}
